// ─────────────────────────────────────────────────────────
// hooks/useOffline.ts — Online/offline detection hook
//
// Phase 6: delegates reconnect + sync to syncEngine.ts so
// there is exactly ONE place that owns the flush → refetch
// → timestamp cycle. The hook now only:
//   1. Reflects isOnline state from authStore
//   2. Registers the syncEngine reconnect handler (once)
//   3. Starts the queue-size poller while mounted
//
// Phase 3 behaviour (flush + invalidate) is preserved —
// it lives inside syncEngine.syncNow().
// ─────────────────────────────────────────────────────────
import { useEffect }    from 'react'
import { useAuthStore } from '@/store/authStore'
import {
  registerReconnectHandler,
  startQueuePoll,
  getCachedQueueSize,
  getLastSyncAt,
} from '@/services/syncEngine'

export function useOffline() {
  const { isOnline, setOnline } = useAuthStore()

  // Register the canonical reconnect handler (idempotent)
  useEffect(() => {
    const unregister = registerReconnectHandler()
    return unregister
  }, [])

  // Reflect browser online/offline events in authStore
  useEffect(() => {
    const handleOnline  = () => setOnline(true)
    const handleOffline = () => setOnline(false)
    window.addEventListener('online',  handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online',  handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [setOnline])

  // Poll queue size while mounted
  useEffect(() => {
    const stop = startQueuePoll()
    return stop
  }, [])

  return {
    isOnline,
    isOffline:  !isOnline,
    queueSize:  getCachedQueueSize(),
    lastSyncAt: getLastSyncAt(),
  }
}
